import { useMemo } from 'react';
import { useSearchParams } from 'react-router';

import type { SearchRefinementInput } from '#src/__generated__/globalTypes.ts';
import {
  SearchFaceting,
  SearchOperator,
} from '#src/__generated__/globalTypes.ts';
import { useIntlContext } from '#src/contexts/intl/index.ts';

import type { RefinementType } from './index.tsx';

type FilterConfig = Record<
  string,
  { api: string | ((lang: string) => string); type: RefinementType }
>;

type ResolvedFilter = readonly [
  urlAttribute: string,
  apiAttribute: string,
  type: RefinementType,
];

function resolveFilters(
  filterConfig: FilterConfig,
  language: string
): ResolvedFilter[] {
  return Object.entries(filterConfig).map(
    ([urlAttribute, { api: apiAttribute, type }]) =>
      [
        urlAttribute,
        typeof apiAttribute === 'string'
          ? apiAttribute
          : apiAttribute(language),
        type,
      ] as const
  );
}

function buildRefinements(
  activeFacets: Record<string, string[]>,
  urlToType: Record<string, string>,
  urlToApi: Record<string, string>
): SearchRefinementInput[] {
  return Object.entries(activeFacets).flatMap<SearchRefinementInput>(
    ([k, v]) => {
      if (!v.length || urlToType[k] === 'virtual') return [];
      if (urlToType[k] === 'numeric') {
        return v
          .map(
            (value, i) =>
              value && {
                field: urlToApi[k],
                operator: i
                  ? SearchOperator.LESS_THAN_OR_EQUAL
                  : SearchOperator.GREATER_THAN_OR_EQUAL,
                values: [{ integerValue: Number.parseInt(value, 10) }],
              }
          )
          .filter(Boolean);
      }
      return {
        field: urlToApi[k],
        operator: SearchOperator.EQUAL,
        values: v.map(vv => ({ stringValue: vv })),
      };
    }
  );
}

export function useGQLSearch(filterConfig: FilterConfig) {
  const { language } = useIntlContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const resolvedFilters = useMemo(
    () => resolveFilters(filterConfig, language),
    [filterConfig, language]
  );

  const urlToApi = useMemo(
    () => Object.fromEntries(resolvedFilters),
    [resolvedFilters]
  );
  const urlToType = useMemo(
    () => Object.fromEntries(resolvedFilters.map(e => [e[0], e[2]])),
    [resolvedFilters]
  );
  const apiToUrl = useMemo(
    () => Object.fromEntries(resolvedFilters.map(e => e.slice(0, 2).reverse())),
    [resolvedFilters]
  );

  const query = searchParams.get('q') ?? '';
  const page = Number.parseInt(searchParams.get('p') ?? '1', 10);

  const activeFacets = useMemo(
    () =>
      Object.fromEntries(
        resolvedFilters.map(e => [
          e[0],
          searchParams
            .getAll(e[0])
            .flatMap(e2 =>
              urlToType[e[0]] === 'numeric'
                ? decodeURIComponent(e2).split(':')
                : e2.split(',')
            )
            .map(decodeURIComponent),
        ])
      ),
    [resolvedFilters, searchParams, urlToType]
  );

  const refinements = useMemo(
    () => buildRefinements(activeFacets, urlToType, urlToApi),
    [activeFacets, urlToType, urlToApi]
  );

  const facets = useMemo(
    () =>
      resolvedFilters
        .filter(e => urlToType[e[0]] !== 'virtual')
        .map(e => ({
          field: e[1],
          limit: 20,
          type: SearchFaceting.DISJUNCTIVE,
        })),
    [resolvedFilters, urlToType]
  );

  return {
    urlToApi,
    urlToType,
    apiToUrl,
    activeFacets,
    refinements,
    facets,
    query,
    page,
    setSearchParams,
    searchParams,
  };
}
